"use client";

import { ChatMessage } from "@/app/api/chats/[chatId]/messages/messages.dto";
import { MentionProp } from "@/types/post";
import MiniEditor from "@/components/common/editor/mini-editor";
import { CHATS_API_BASE_URL, CHAT_DIRECT_MESSAGING_API } from "@/lib/constants";
import { formatImageUrl } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useChat } from "@/providers/chat-provider";
import { useModal } from "@/providers/modal-provider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SendIcon } from "lucide-react";
import Link from "next/link";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { v4 as uuid } from "uuid";
import { ModalType } from "@/types/modal-type";

const ChatInput = () => {
	const tempId = uuid();
	const { activeChat } = useChat();
	const queryClient = useQueryClient();
	const { isLoggedIn, user } = useAuth();
	const { openModal } = useModal();
	const [message, setMessage] = useState<string>("");
	const [mentions, setMentions] = useState<MentionProp[]>([]);

	const defaultEmptyMessageString = "<p></p>";

	const isVIPUser = user?.isFounder || false;
	const isVerifiedUser = user?.isVerified || false;
	const isVIPChat = activeChat?.rules?.isVIPChat || false;

	const formRef = useRef<HTMLFormElement | null>(null);

	// TODO (ruthvik): Fix types
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const messageInputRef = useRef<any | null>(null);

	const queryKey = "chatMessages";
	const uniqueChatId = activeChat?.uniqueChatId;
	const isGroupChat = activeChat?.type === "group";

	const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (message.length === 0 || message === defaultEmptyMessageString) {
			return;
		}

		messageMutation.mutate({
			messageText: message,
			receiverId: activeChat?.recipient?.id,
		});

		setMessage("");
	};

	const sendMessageMutation = async ({
		messageText,
		receiverId,
	}: {
		messageText: string;
		receiverId: string | undefined;
	}): Promise<Partial<ChatMessage>> => {
		try {
			const url =
				isGroupChat && uniqueChatId
					? new URL(`${CHATS_API_BASE_URL}/${uniqueChatId}/messages`)
					: new URL(CHAT_DIRECT_MESSAGING_API);

			const body = isGroupChat ? { messageText, mentions } : { messageText, receiverId };

			const response = await fetch(url, {
				method: "POST",
				body: JSON.stringify(body),
			});

			const { data, message } = await response.json();

			if (!response.ok) {
				toast.error(message || "Something went wrong");
			}

			return {
				id: data?.id,
				messageText: data?.messageText,
				sender: {
					id: user?.id || "",
					name: user?.name || "",
					username: user?.username || "",
					image: user?.image || "",
				},
				updatedAt: data?.updatedAt,
			};
		} catch (error) {
			console.log("Error occured while sending message");

			return {
				id: undefined,
				messageText: undefined,
				sender: undefined,
				updatedAt: undefined,
			};
		}
	};

	const messageMutation = useMutation({
		mutationFn: ({
			messageText,
			receiverId,
		}: {
			messageText: string;
			receiverId: string | undefined;
		}) => sendMessageMutation({ messageText, receiverId }),

		onMutate: async (newMessage) => {
			if (messageInputRef.current?.clearEditorContent) {
				messageInputRef.current.clearEditorContent();
			}

			await queryClient.cancelQueries({ queryKey: [queryKey, uniqueChatId] });

			const previousMessages = queryClient.getQueryData<ChatMessage[]>([queryKey, uniqueChatId]);

			queryClient.setQueryData(
				[queryKey, uniqueChatId],
				(prevMessages: Partial<ChatMessage>[] | undefined) => {
					return prevMessages
						? [
								...prevMessages,
								{
									id: tempId,
									messageText: newMessage.messageText,
									sender: {
										id: user?.id || "",
										name: user?.name,
										username: user?.username || "",
										image: formatImageUrl(user?.image),
										incognito: activeChat?.rules?.isIncognitoChat || false,
									},
									updatedAt: new Date(),
								},
						  ]
						: [
								{
									id: tempId,
									messageText: newMessage.messageText,
									sender: {
										id: user?.id || "",
										name: user?.name,
										username: user?.username || "",
										image: formatImageUrl(user?.image),
										incognito: activeChat?.rules?.isIncognitoChat || false,
									},
									updatedAt: new Date(),
								},
						  ];
				}
			);

			return { previousMessages };
		},
		onError: (_, __, context) => {
			queryClient.setQueryData([queryKey, uniqueChatId], context?.previousMessages);
		},
		onSettled: (data) => {
			queryClient.invalidateQueries(["inbox", "messages"]);
			queryClient.refetchQueries(["inbox"]);

			const newMessage = data;

			queryClient.setQueryData(
				[queryKey, uniqueChatId],
				(prevMessages: Partial<ChatMessage>[] | undefined) =>
					prevMessages?.map((message) =>
						message.id === tempId ? { ...message, id: newMessage?.id } : message
					)
			);
		},
	});

	const handleMessageChange = ({
		content,
		mentions = [],
	}: {
		content: string;
		mentions?: MentionProp[];
	}) => {
		setMessage(content);
		setMentions(mentions);
	};

	return !isLoggedIn ? (
		<button
			type="button"
			onClick={() => openModal(ModalType.Auth)}
			className="block w-full rounded-md border border-brand-primary py-2 text-center font-medium text-brand-primary"
		>
			Sign In to continue
		</button>
	) : isLoggedIn && !isVerifiedUser ? (
		<Link
			href="/account/profile"
			className="block w-full rounded-md border border-brand-primary py-2 text-center font-medium text-brand-primary"
		>
			Verify to continue
		</Link>
	) : isVIPChat && !isVIPUser ? (
		<div className="block w-full rounded-md border border-brand-primary py-2 text-center font-medium text-brand-primary">
			You must be a VIP to continue
		</div>
	) : (
		<form ref={formRef} onSubmit={handleSendMessage}>
			<div className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-200 py-1">
				<MiniEditor
					content={message}
					formRef={formRef}
					editorRef={messageInputRef}
					placeholder="Type a message"
					handleOnUpdate={handleMessageChange}
					classNames={{
						container: "px-2 flex items-center gap-3 w-full",
						editor:
							"w-full rounded-lg outline-none disabled:cursor-not-allowed min-h-min max-h-20 overflow-y-auto thin-scrollbar mt-0",
					}}
					disableOptions={[
						"Link",
						"Bold",
						"Italic",
						"Strike",
						"Paragraph",
						"Heading 1",
						"Heading 2",
						"Heading 3",
						"YouTube",
						"Bullet List",
						"Ordered List",
						"Divider",
						"Line Break",
						"Undo",
						"Redo",
					]}
				/>

				<div>
					<button
						className="mx-2 rounded-xl bg-slate-900 p-3 text-white outline-none disabled:cursor-not-allowed disabled:opacity-75"
						aria-label="Send Message"
						disabled={!message.length || message === defaultEmptyMessageString}
					>
						<SendIcon className="h-5 w-5" />
					</button>
				</div>
			</div>
		</form>
	);
};

export default ChatInput;
